import React, { useState, useEffect, useCallback } from "react";
import { Car, Plus, Trash2, Check, X, Pencil, ChevronDown } from "lucide-react";
import axios from "axios";
import { API, authConfig, RUC_WEIGHT_BANDS } from "./constants";
import { toast } from "sonner";

const VEHICLE_TYPES = {
  car: { label: "Car", icon: "🚗", hint: "6-8 L/100km", defaultRuc: "light" },
  suv: { label: "SUV", icon: "🚙", hint: "10-14 L/100km", defaultRuc: "light" },
  truck: { label: "Truck", icon: "🛻", hint: "12-18 L/100km", defaultRuc: "medium" },
  van: { label: "Van", icon: "🚐", hint: "10-16 L/100km", defaultRuc: "light" },
  motorcycle: { label: "Motorcycle", icon: "🏍", hint: "3-6 L/100km", defaultRuc: "light" },
};

export default function VehicleSelector({ user, selectedVehicle, onSelectVehicle, onConsumptionChange }) {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formName, setFormName] = useState("");
  const [formConsumption, setFormConsumption] = useState("");
  const [formType, setFormType] = useState("car");
  const [formRucTier, setFormRucTier] = useState("light");

  const fetchVehicles = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/vehicles/`, authConfig());
      setVehicles(resp.data);
    } catch (err) {
      console.error("Failed to fetch vehicles:", err);
    } finally { setLoading(false); }
  }, [user]);

  useEffect(() => { fetchVehicles(); }, [fetchVehicles]);

  const handleSave = async () => {
    if (!formName.trim() || !formConsumption || parseFloat(formConsumption) <= 0) {
      toast.error("Please fill in all fields"); return;
    }
    try {
      const payload = {
        name: formName.trim(),
        consumption_l_per_100km: parseFloat(formConsumption),
        vehicle_type: formType,
        ruc_tier: formRucTier,
      };
      if (editingId) {
        await axios.put(`${API}/vehicles/${editingId}`, payload, authConfig());
        toast.success("Vehicle updated");
      } else {
        await axios.post(`${API}/vehicles/`, payload, authConfig());
        toast.success("Vehicle added");
      }
      resetForm();
      fetchVehicles();
    } catch { toast.error("Failed to save vehicle"); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/vehicles/${id}`, authConfig());
      if (selectedVehicle?.id === id) onSelectVehicle(null);
      fetchVehicles();
      toast.success("Vehicle deleted");
    } catch { toast.error("Failed to delete"); }
  };

  const startEdit = (v) => {
    setEditingId(v.id);
    setFormName(v.name);
    setFormConsumption(v.consumption_l_per_100km.toString());
    setFormType(v.vehicle_type);
    setFormRucTier(v.ruc_tier || VEHICLE_TYPES[v.vehicle_type]?.defaultRuc || "light");
    setShowAddForm(true);
    setShowDropdown(false);
  };

  const resetForm = () => {
    setShowAddForm(false);
    setEditingId(null);
    setFormName("");
    setFormConsumption("");
    setFormType("car");
    setFormRucTier("light");
  };

  const selectVehicle = (v) => {
    onSelectVehicle(v);
    onConsumptionChange(v.consumption_l_per_100km.toString());
    setShowDropdown(false);
    toast.success(`Using ${v.name} (${v.consumption_l_per_100km} L/100km)`);
  };

  const clearVehicle = () => {
    onSelectVehicle(null);
    setShowDropdown(false);
  };

  if (!user) return null;

  return (
    <div className="mb-5" data-testid="vehicle-selector">
      <label className="sf-label"><Car className="w-4 h-4" />MY VEHICLES</label>

      {/* Vehicle select button */}
      <div className="relative" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={() => { setShowDropdown(!showDropdown); setShowAddForm(false); }}
          className="sf-input w-full text-left flex items-center justify-between cursor-pointer hover:border-[#E52020] transition-colors"
          data-testid="vehicle-dropdown-btn"
        >
          <span className={`flex-1 min-w-0 truncate ${selectedVehicle ? "text-[#1A1A1A]" : "text-[#999999]"}`}>
            {selectedVehicle
              ? `${VEHICLE_TYPES[selectedVehicle.vehicle_type]?.icon || "🚗"} ${selectedVehicle.name} — ${selectedVehicle.consumption_l_per_100km} L/100km`
              : "Select a vehicle"}
          </span>
          <ChevronDown className={`w-4 h-4 flex-shrink-0 text-[#666666] transition-transform ${showDropdown ? "rotate-180" : ""}`} />
        </button>

        {/* Dropdown */}
        {showDropdown && (
          <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-[rgba(229, 32, 32,0.15)] rounded-sm shadow-lg overflow-hidden" data-testid="vehicle-dropdown-list">
            {loading ? (
              <div className="p-4 text-center text-[#666666] font-mono text-sm">Loading...</div>
            ) : vehicles.length === 0 ? (
              <div className="p-4 text-center text-[#999999] text-sm">
                <Car className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="font-['Manrope']">No vehicles yet</p>
              </div>
            ) : (
              vehicles.map((v) => (
                <div key={v.id}
                  className={`flex items-center justify-between px-4 py-3 hover:bg-[rgba(229, 32, 32,0.04)] cursor-pointer transition-colors border-b border-[#F7F5F0] ${selectedVehicle?.id === v.id ? "bg-[rgba(229, 32, 32,0.06)]" : ""}`}
                  data-testid={`vehicle-option-${v.id}`}
                >
                  <div className="flex-1 min-w-0" onClick={() => selectVehicle(v)}>
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{VEHICLE_TYPES[v.vehicle_type]?.icon || "🚗"}</span>
                      <span className="font-['Manrope'] font-semibold text-sm text-[#1A1A1A] truncate">{v.name}</span>
                      {selectedVehicle?.id === v.id && <Check className="w-4 h-4 text-[#E52020]" />}
                    </div>
                    <p className="text-xs text-[#666666] font-mono ml-7">{v.consumption_l_per_100km} L/100km • {VEHICLE_TYPES[v.vehicle_type]?.label || v.vehicle_type}</p>
                  </div>
                  <div className="flex items-center gap-1 ml-2">
                    <button onClick={(e) => { e.stopPropagation(); startEdit(v); }} className="p-1.5 text-[#666666] hover:text-[#E52020] hover:bg-[rgba(229, 32, 32,0.06)] rounded-sm transition-colors" data-testid={`edit-vehicle-${v.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); handleDelete(v.id); }} className="p-1.5 text-[#666666] hover:text-[#DC2626] hover:bg-[rgba(220,38,38,0.06)] rounded-sm transition-colors" data-testid={`delete-vehicle-${v.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))
            )}
            {/* Clear selection */}
            {selectedVehicle && (
              <button onClick={clearVehicle} className="w-full px-4 py-2.5 text-sm text-[#666666] hover:bg-[rgba(229, 32, 32,0.04)] text-left font-['Manrope'] border-b border-[#F7F5F0]">
                Clear selection (manual entry)
              </button>
            )}
            {/* Add new */}
            <button onClick={() => { setShowAddForm(true); setShowDropdown(false); }} className="w-full px-4 py-3 flex items-center gap-2 text-sm text-[#E52020] hover:bg-[rgba(229, 32, 32,0.04)] font-['Manrope'] font-semibold" data-testid="add-vehicle-btn">
              <Plus className="w-4 h-4" />Add Vehicle
            </button>
          </div>
        )}
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="mt-3 p-4 bg-[rgba(229, 32, 32,0.03)] border border-[rgba(229, 32, 32,0.12)] rounded-sm" data-testid="vehicle-form">
          <p className="text-xs font-bold text-[#666666] tracking-wider mb-3 font-['Manrope']">
            {editingId ? "EDIT VEHICLE" : "ADD VEHICLE"}
          </p>
          <input type="text" value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="Vehicle name (e.g., Family SUV)" className="sf-input w-full text-sm mb-3" data-testid="vehicle-name-input" />
          <div className="mb-3">
            <label className="text-xs font-bold text-[#666666] tracking-wider mb-1 block font-['Manrope']">CONSUMPTION (L/100KM)</label>
            <input type="number" value={formConsumption} onChange={(e) => setFormConsumption(e.target.value)} placeholder="e.g., 8" className="sf-input w-full" data-testid="vehicle-consumption-input" min="0" step="0.1" />
          </div>
          <div className="mb-3">
            <label className="text-xs font-bold text-[#666666] tracking-wider mb-1 block font-['Manrope']">VEHICLE TYPE</label>
            <select value={formType} onChange={(e) => { const t = e.target.value; setFormType(t); setFormRucTier(VEHICLE_TYPES[t]?.defaultRuc || "light"); }} className="sf-input w-full text-sm" data-testid="vehicle-type-select">
              {Object.entries(VEHICLE_TYPES).map(([key, vt]) => {
                const optionText = `${vt.icon} ${vt.label} (${vt.hint})`;
                return <option key={key} value={key}>{optionText}</option>;
              })}
            </select>
          </div>
          <div className="mb-3">
            <label className="text-xs font-bold text-[#666666] tracking-wider mb-1 block font-['Manrope']">DEFAULT RUC TIER <span className="text-[#999999] font-normal normal-case tracking-normal">(used when fuel is diesel)</span></label>
            <select value={formRucTier} onChange={(e) => setFormRucTier(e.target.value)} className="sf-input w-full text-sm" data-testid="vehicle-ruc-tier-select">
              {RUC_WEIGHT_BANDS.map(band => {
                const optionText = `${band.label} — $${band.rate}/1,000km`;
                return <option key={band.key} value={band.key}>{optionText}</option>;
              })}
            </select>
          </div>
          <div className="flex gap-2">
            <button onClick={handleSave} className="sf-primary-btn flex-1 text-sm py-2" data-testid="vehicle-save-btn">
              <Check className="w-4 h-4" />{editingId ? "UPDATE" : "ADD"}
            </button>
            <button onClick={resetForm} className="px-4 py-2 text-sm text-[#666666] border border-[#EBE6DC] rounded-sm hover:bg-[#F7F5F0] transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
